import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { EnvelopeSimple, Globe, ChatCircleDots, Clock } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

export default function Support() {
  const t = useT();
  useSeo({
    title: "Support",
    description: "Talk to a human — email, phone, or live chat. Mon–Sat, 8am–8pm PT. Answers to common virtual card questions.",
    path: "/support",
  });
  const [info, setInfo] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/support/info").then((r) => setInfo(r.data)).catch(() => setInfo({}));
  }, []);

  const email = info?.email || "support@zcardon.com";
  const phone = info?.phone || "+1 (415) 555-0140";
  const phoneHref = `tel:${phone.replace(/[^\d+]/g, "")}`;
  const hours = info?.hours || "Mon–Sat · 8am–8pm PT";

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="support-page">
      <Header />

      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-4">{t("support.eyebrow")}</p>
            <h1 className="serif text-5xl md:text-6xl leading-[1.05]">{t("support.title")}</h1>
            <p className="text-[#4A5C50] mt-6 leading-relaxed">
              {t("support.subtitle")}
            </p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-6">
            <InfoCard icon={<EnvelopeSimple size={26} weight="duotone" />} k={t("support.card.email")} v={email} href={`mailto:${email}`} testId="support-email" />
            <InfoCard icon={<Globe size={26} weight="duotone" />} k={t("support.card.phone")} v={phone} href={phoneHref} testId="support-phone" />
            <InfoCard icon={<ChatCircleDots size={26} weight="duotone" />} k={t("support.card.live_chat")} v={t("support.card.live_chat_value")} testId="support-chat" />
            <InfoCard icon={<Clock size={26} weight="duotone" />} k={t("support.card.hours")} v={hours} testId="support-hours" />
          </div>
        </div>

        <div className="mt-20 bg-white border border-[#E5E0D8] rounded-3xl p-8 md:p-12" data-testid="support-faq">
          <h2 className="serif text-3xl md:text-4xl">{t("support.faq.title")}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-x-12 gap-y-8">
            <Faq q={t("support.faq.q1")} a={t("support.faq.a1")} />
            <Faq q={t("support.faq.q2")} a={t("support.faq.a2")} />
            <Faq q={t("support.faq.q3")} a={t("support.faq.a3")} />
            <Faq q={t("support.faq.q4")} a={t("support.faq.a4")} />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-10 text-center text-sm text-[#4A5C50]">
        <div className="flex items-center justify-center gap-6 mb-3">
          <Link to="/" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-home">{t("header.nav.home")}</Link>
          <Link to="/rates" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-rates">{t("landing.footer.rates")}</Link>
          <Link to="/login?role=admin" className="link-ul text-xs tracking-widest uppercase" data-testid="footer-admin-portal">{t("landing.footer.admin_portal")}</Link>
        </div>
        {t("landing.footer.copy")}
      </footer>
    </div>
  );
}

function InfoCard({ icon, k, v, href, testId }) {
  const body = (
    <>
      <div className="text-[#184A30]">{icon}</div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-3">{k}</p>
      <p className="serif text-2xl mt-1">{v}</p>
    </>
  );
  const cls = "bg-white border border-[#E5E0D8] rounded-2xl p-6 card-lift block";
  return href ? (
    <a href={href} className={cls} data-testid={testId}>{body}</a>
  ) : (
    <div className={cls} data-testid={testId}>{body}</div>
  );
}

function Faq({ q, a }) {
  return (
    <div>
      <p className="serif text-lg text-[#112318]">{q}</p>
      <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">{a}</p>
    </div>
  );
}
